import type {PackageSubscriptionSerializer} from "@/generated/DjangoTypes";
import type {TSubscriptionWithDiscountOptions} from "@/store/types";
import type {TPlanFeatureKey} from "@/utils/planUtils";
import omitBy from "lodash/omitBy";
import pick from "lodash/pick";

const ADD_ON_FEATURE_KEYS = [
  "tiktok_visibility_country_count",
  "tiktok_visibility_keyword_limit",
  "tiktok_visibility_sync_interval_hours",
] as const;

type TPlan = (PackageSubscriptionSerializer | TSubscriptionWithDiscountOptions) & {
  access_to_ad_codes?: boolean;
  account_setup_call?: boolean;
  customer_success?: boolean;
};

type TPlanCategoryKey = "campaigns" | "content_and_rights" | "creator_network" | "onboarding";

const PLAN_CATEGORIES: {
  features: TPlanFeatureKey[];
  key: TPlanCategoryKey;
}[] = [
  {
    features: ["influencers_per_campaign", "number_of_multiple_active_campaigns", "invitations_per_campaign"],
    key: "campaigns",
  },
  {
    features: ["buyout", "access_to_ad_codes", "content_approval"],
    key: "content_and_rights",
  },
  {
    features: ["access_to_influencer_network", "pro_creator_mode", "social_feed_filters"],
    key: "creator_network",
  },
  {
    features: ["account_setup_call", "customer_success"],
    key: "onboarding",
  },
] as const;

const usePlanPerks = (plan: TPlan | null | undefined) => {
  const pickedKeys = pick(
    plan,
    PLAN_CATEGORIES.flatMap(c => c.features),
  );
  const fixKeys = omitBy(
    pickedKeys,
    (value, key) => !value || (key === "number_of_multiple_active_campaigns" && value === 1),
  );
  const unlockedFeatures =
    "influencer_level_insights" in fixKeys ? {...fixKeys, influencer_demographics: true} : fixKeys;
  const pickedAddOnFeatures = pick(plan, ADD_ON_FEATURE_KEYS);
  const unlockedAddOnFeatures = omitBy(pickedAddOnFeatures, value => !value);
  const lockedAddOnFeatures = omitBy(pickedAddOnFeatures, value => value);
  const lockedFeatures = omitBy(pickedKeys, value => value);

  const categories = PLAN_CATEGORIES.map(category => ({
    features: category.features.map(key => ({key, value: pickedKeys[key]})),
    key: category.key,
  }));

  return {categories, lockedAddOnFeatures, lockedFeatures, unlockedAddOnFeatures, unlockedFeatures};
};

export {ADD_ON_FEATURE_KEYS, usePlanPerks};
